/**
 * The viewer's odds-format preference, persisted per browser. A tiny external store rather than
 * a context: every price cell reads it, and the toggle flips the whole board in one render.
 */
import { useSyncExternalStore } from "react";
import type { OddsFormat } from "./format";

const KEY = "omodds.oddsFormat";
const VALID: readonly OddsFormat[] = ["american", "decimal", "cents", "percent"];

export function isOddsFormat(v: string): v is OddsFormat {
  return (VALID as readonly string[]).includes(v);
}

function read(): OddsFormat {
  try {
    const v = localStorage.getItem(KEY);
    return v && isOddsFormat(v) ? v : "american";
  } catch {
    return "american";
  }
}

let current: OddsFormat = typeof window === "undefined" ? "american" : read();
const listeners = new Set<() => void>();

export function setOddsFormat(next: OddsFormat): void {
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Private mode or storage blocked: the choice still holds for this visit.
  }
  for (const l of listeners) l();
}

export function useOddsFormat(): OddsFormat {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => current,
    () => "american",
  );
}
